import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { markNotificationsAsRead } from '../../../../api';
import parseNotificationMessage from '../../../../utils/parseNotificationMessage';
import { isMobile } from '../../../../utils/mobile';

const HIDE_DURATION = 380;
const HEIGHT_STORAGE_KEY = 'notif_banner_h';

const NotificationsBanner = ({ user }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(!!user?.token);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hidingIds, setHidingIds] = useState(new Set());
  const touchStartX = useRef(null);
  const bannerRef = useRef(null);
  // Read stored height once on mount to avoid layout jump
  const [reservedHeight] = useState(() => {
    try { return parseInt(localStorage.getItem(HEIGHT_STORAGE_KEY) || '0', 10); } catch { return 0; }
  });
  const history = useHistory();

  // Save banner height to localStorage whenever it renders with real content
  useEffect(() => {
    if (notifications.length > 0 && bannerRef.current) {
      const h = bannerRef.current.offsetHeight;
      if (h > 0) {
        try { localStorage.setItem(HEIGHT_STORAGE_KEY, String(h)); } catch {}
      }
    }
  }, [notifications.length]);

  useEffect(() => {
    if (!user?.token) { setLoading(false); return; }
    const handleLoaded = (e) => {
      setNotifications(e.detail.notifications.filter((n) => !n.read_at));
      setLoading(false);
    };
    window.addEventListener('headerNotificationsLoaded', handleLoaded);
    return () => window.removeEventListener('headerNotificationsLoaded', handleLoaded);
  }, [user?.token]);

  useEffect(() => {
    const handleAllRead = () => setNotifications([]);
    window.addEventListener('notificationsAllRead', handleAllRead);
    return () => window.removeEventListener('notificationsAllRead', handleAllRead);
  }, []);

  useEffect(() => {
    const handleOneRead = (e) => {
      const { id } = e.detail;
      setNotifications((prev) => {
        const next = prev.filter((n) => n.id !== id);
        setCurrentIndex((ci) => Math.min(ci, Math.max(0, next.length - 1)));
        return next;
      });
    };
    window.addEventListener('notificationRead', handleOneRead);
    return () => window.removeEventListener('notificationRead', handleOneRead);
  }, []);

  const handleRead = useCallback((id) => {
    setHidingIds((prev) => new Set([...prev, id]));
    markNotificationsAsRead(id).catch(() => {});
    setTimeout(() => {
      setNotifications((prev) => {
        const next = prev.filter((n) => n.id !== id);
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('bannerNotificationRead', { detail: { id, remainingUnread: next.length } }));
        }, 0);
        setCurrentIndex((ci) => Math.min(ci, Math.max(0, next.length - 1)));
        return next;
      });
      setHidingIds((prev) => {
        const s = new Set(prev);
        s.delete(id);
        return s;
      });
    }, HIDE_DURATION);
  }, []);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) {
        setCurrentIndex((i) => Math.min(notifications.length - 1, i + 1));
      } else {
        setCurrentIndex((i) => Math.max(0, i - 1));
      }
    }
    touchStartX.current = null;
  }, [notifications.length]);

  if (!user?.token) return null;

  // While waiting for API: show a same-sized skeleton so the layout doesn't jump
  if (loading && reservedHeight > 0) {
    return (
      <div
        className="notif-banner notif-banner--skeleton"
        style={{ height: reservedHeight }}
        aria-hidden="true"
      />
    );
  }

  if (notifications.length === 0) {
    // No unread notifications — clear the stored height so we don't reserve space next time
    try { localStorage.removeItem(HEIGHT_STORAGE_KEY); } catch {}
    return null;
  }

  const prev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const next = () => setCurrentIndex((i) => Math.min(notifications.length - 1, i + 1));

  return (
    <div ref={bannerRef} className="notif-banner">
      <div
        className="notif-banner__overflow"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="notif-banner__track"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {notifications.map((notif) => (
            <div
              key={notif.id}
              className={`notif-banner__slide${hidingIds.has(notif.id) ? ' notif-banner__slide--hiding' : ''}`}
            >
              <div className="notif-banner__icon">
                <i className="fas fa-bell" />
              </div>
              <div className="notif-banner__body">
                {parseNotificationMessage(notif.message || notif.body, history.push)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="notif-banner__footer">
        <div className="notif-banner__dots-wrap">
          {notifications.length > 1 && (
            <>
              <button
                className="notif-banner__arrow"
                onClick={prev}
                disabled={currentIndex === 0}
                aria-label="Попереднє"
              >
                <i className="fas fa-chevron-left" />
              </button>
              {notifications.map((_, i) => (
                <span
                  key={i}
                  className={`notif-banner__dot${i === currentIndex ? ' notif-banner__dot--active' : ''}`}
                  onClick={() => setCurrentIndex(i)}
                />
              ))}
              <button
                className="notif-banner__arrow"
                onClick={next}
                disabled={currentIndex === notifications.length - 1}
                aria-label="Наступне"
              >
                <i className="fas fa-chevron-right" />
              </button>
            </>
          )}
        </div>
        <div className="notif-banner__read-wrap">
          <button
            className="notif-banner__read-btn"
            onClick={() => {
              const current = notifications[currentIndex];
              if (current) handleRead(current.id);
            }}
          >
            <i className="fas fa-check" />
            Прочитано
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationsBanner;
